import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, ClipboardList, FileText, Library, Loader2, Trash2, type LucideIcon } from 'lucide-react';
import { cn } from 'lib/utils';
import { formatRelativeTime } from 'lib/relativeTime';
import { Badge } from 'components/ui/badge';
import EmptyState from 'components/ui/EmptyState';
import Skeleton from 'components/ui/skeleton';
import type { LearnArtifact } from 'modules/learnArtifacts';
import { WorkspacePanel } from './WorkspacePanel';

interface StudioPanelProps {
  artifacts: LearnArtifact[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  hasActiveSource: boolean;
  /** The source the tools run against, named in the panel head. */
  activeTitle?: string;
  activeReady: boolean;
  canGenerateBank: boolean;
  /** Document → bank generation is a paid capability the plan doesn't include. */
  bankPremium?: boolean;
  onGenerateQuiz: () => void;
  onGenerateBank: () => void;
  onReview: (a: LearnArtifact) => void;
  onDelete: (a: LearnArtifact) => void;
  deletingId?: string | null;
  onCollapse: () => void;
  /** Phones: the tools and outputs alone, under the screen header. */
  bare?: boolean;
}

interface ToolRowProps {
  icon: LucideIcon;
  label: string;
  desc: string;
  disabled: boolean;
  onClick: () => void;
  /** Paid capability: a neutral "PRO" tag; the click still fires and routes to the upgrade dialog. */
  premium?: boolean;
}

/** A full-width tool: grey 32px icon tile, title and one line, chevron. */
const ToolRow: React.FC<ToolRowProps> = ({ icon: Icon, label, desc, disabled, onClick, premium }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="flex w-full items-center gap-3 rounded-lg border border-border bg-card p-3 text-left transition-[border-color,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 enabled:hover:border-foreground/20 enabled:hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
  >
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground/80">
      <Icon className="h-4 w-4" />
    </span>
    <span className="flex min-w-0 flex-1 flex-col gap-0.5 leading-tight">
      <span className="flex items-center gap-2">
        <span className="truncate text-[13.5px] font-semibold text-foreground">{label}</span>
        {premium && <Badge className="shrink-0">PRO</Badge>}
      </span>
      <span className="text-xs leading-tight text-muted-foreground">{desc}</span>
    </span>
    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/70" />
  </button>
);

/** One ink segment per batch; long runs share a 120px track. */
const BatchProgress: React.FC<{ done: number; total: number }> = ({ done, total }) => (
  <span aria-hidden data-batch-progress className="flex shrink-0 gap-[3px]" style={{ width: Math.min(total * 21 - 3, 120) }}>
    {Array.from({ length: total }, (_, i) => (
      <span key={i} className={cn('h-1 min-w-0 flex-1 rounded-full', i < done ? 'bg-foreground' : 'bg-border')} />
    ))}
  </span>
);

const ArtifactRow: React.FC<{
  artifact: LearnArtifact;
  onReview: (a: LearnArtifact) => void;
  onDelete: (a: LearnArtifact) => void;
  deleting: boolean;
}> = ({ artifact, onReview, onDelete, deleting }) => {
  const { t, i18n } = useTranslation();
  const generating = artifact.status === 'generating';
  const failed = artifact.status === 'failed';
  const isBankBatch = artifact.type === 'bank_batch';
  const kind = isBankBatch ? t('aigen.tool_bank') : t('aigen.tool_quiz');
  const KindIcon = isBankBatch ? Library : ClipboardList;

  // Large drafts are generated as concurrent batches and report as each lands,
  // so a multi-minute run shows movement instead of a bare spinner.
  const bankMeta = artifact.metadata?.bank as
    | { progress?: { done: number; total: number }; questionsAdded?: number; questionsGenerated?: number }
    | undefined;
  const progress = bankMeta?.progress;
  const batched = Boolean(progress && progress.total > 1);

  const hasDraft = Boolean((artifact.payload as { questions?: unknown[] } | undefined)?.questions?.length);
  const alreadyBanked = isBankBatch && !hasDraft && typeof bankMeta?.questionsAdded === 'number';
  const reviewable = artifact.status === 'ready' && (isBankBatch ? hasDraft : true);

  const questionCount =
    (artifact.payload as { questions?: unknown[] } | undefined)?.questions?.length ??
    bankMeta?.questionsGenerated;

  const countSubtitle =
    typeof questionCount === 'number' && questionCount > 0
      ? t('library.questions_count', { count: questionCount })
      : alreadyBanked
        ? t('questions_added_count', { count: bankMeta?.questionsAdded ?? 0 })
        : kind;
  // Same-titled drafts are common (one per run), so the age is what tells them apart.
  const age = formatRelativeTime(artifact.createdAt, i18n?.language);

  return (
    <li className="px-4 py-1.5">
      <div className="group flex items-start gap-3 rounded-xl border border-border bg-card p-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground/80">
          <KindIcon className="h-4 w-4" aria-hidden />
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="line-clamp-2 text-[13.5px] font-semibold leading-snug text-foreground [overflow-wrap:anywhere]">
            {artifact.title || kind}
          </p>
          <div className="text-xs leading-tight text-muted-foreground">
            {generating ? (
              batched && progress ? (
                <span className="flex items-center gap-2">
                  <BatchProgress done={progress.done} total={progress.total} />
                  <span className="min-w-0">{t('generating_batches', { done: progress.done, total: progress.total })}</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {t('generating')}
                </span>
              )
            ) : failed ? (
              <span className="line-clamp-2 text-error-700" title={artifact.errorMessage || undefined}>
                {artifact.errorMessage || t('generation_failed')}
              </span>
            ) : (
              <>
                {countSubtitle}
                {age && <span aria-hidden> · </span>}
                {age}
              </>
            )}
          </div>

          {reviewable && (
            <button
              type="button"
              onClick={() => onReview(artifact)}
              className="mt-2 inline-flex h-9 w-fit items-center rounded-md border border-border bg-card px-3 text-[12.5px] font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('pipeline_review')} <span aria-hidden className="ml-1">→</span>
            </button>
          )}
        </div>

        {!generating && (
          // Quiet until the card is hovered or focused on desktop; always there on phones.
          <button
            type="button"
            onClick={() => onDelete(artifact)}
            disabled={deleting}
            aria-label={t('delete')}
            title={t('delete')}
            className={cn(
              'relative -mr-1 -mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-[opacity,color,background-color] before:absolute before:-inset-1.5 hover:bg-muted hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-100',
              'md:opacity-0 md:[.group:focus-within_&]:opacity-100 md:[.group:hover_&]:opacity-100',
            )}
          >
            {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>
    </li>
  );
};

export const StudioPanel: React.FC<StudioPanelProps> = ({
  artifacts,
  isLoading,
  isError,
  onRetry,
  hasActiveSource,
  activeTitle,
  activeReady,
  canGenerateBank,
  bankPremium,
  onGenerateQuiz,
  onGenerateBank,
  onReview,
  onDelete,
  deletingId,
  onCollapse,
  bare = false,
}) => {
  const { t } = useTranslation();
  const toolsDisabled = !hasActiveSource || !activeReady;

  const renderOutputs = () => {
    if (isLoading) {
      return (
        <div aria-hidden>
          {[0, 1, 2].map((row) => (
            <div key={row} data-output-skeleton className="px-4 py-1.5">
              <div className="flex items-start gap-3 rounded-xl border border-border p-3">
                <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
                <div className="flex flex-1 flex-col gap-1.5 pt-0.5">
                  <Skeleton className={cn('h-3.5', row % 2 ? 'w-1/2' : 'w-3/4')} />
                  <Skeleton className="h-3 w-1/3" />
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }
    if (isError) {
      return (
        <EmptyState
          className="px-4 py-8"
          title={t('failed_to_load')}
          secondaryAction={{ label: t('retry'), onClick: onRetry }}
        />
      );
    }
    if (artifacts.length === 0) {
      return <p className="px-4 py-6 text-center text-[13px] leading-5 text-muted-foreground">{t('aigen.no_outputs')}</p>;
    }
    return (
      <ul className="pb-3">
        {artifacts.map((a) => (
          <ArtifactRow key={a.id} artifact={a} onReview={onReview} onDelete={onDelete} deleting={deletingId === a.id} />
        ))}
      </ul>
    );
  };

  return (
    <WorkspacePanel
      title={t('aigen.studio')}
      meta={activeTitle ? <span className="block max-w-[180px] truncate">{activeTitle}</span> : undefined}
      onCollapse={onCollapse}
      side="right"
      bare={bare}
    >
      {/* Phones have no panel head, so the active source is named above the tools. */}
      {bare && activeTitle && (
        <p className="mx-4 mt-3 flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-[12.5px] text-muted-foreground">
          <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="min-w-0 truncate">{activeTitle}</span>
        </p>
      )}

      {/* Generation tools */}
      <div className="flex shrink-0 flex-col gap-2 px-4 py-3.5">
        <ToolRow
          icon={ClipboardList}
          label={t('aigen.tool_quiz')}
          desc={t('aigen.tool_quiz_desc')}
          disabled={toolsDisabled}
          onClick={onGenerateQuiz}
        />
        {canGenerateBank && (
          <ToolRow
            icon={Library}
            label={t('aigen.tool_bank')}
            desc={t('aigen.tool_bank_desc')}
            disabled={toolsDisabled}
            premium={bankPremium}
            onClick={onGenerateBank}
          />
        )}
      </div>

      {/* Generated outputs */}
      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">
        <h3 className="px-4 pb-1.5 pt-2.5 text-[12.5px] font-semibold leading-tight text-muted-foreground">{t('aigen.outputs')}</h3>
        {renderOutputs()}
      </div>
    </WorkspacePanel>
  );
};

export default StudioPanel;
